import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Archive, ArrowLeft, FolderPlus, Loader2, SearchX } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import api, { clearGalleryCache } from "../lib/api";
import { useDuckActions } from "../hooks/useDuckActions";
import { useFlocks } from "../hooks/useFlocks";
import { usePref } from "../hooks/usePref";
import { DuckDownloadModal } from "../components/DuckDownloadModal";
import { ViewToggle } from "../components/yourducks/ViewToggle";
import { DuckFilters, NO_FILTERS } from "../components/yourducks/DuckFilters";
import { FlockSection } from "../components/yourducks/FlockSection";
import { FlockNameDialog, DeleteFlockDialog } from "../components/yourducks/FlockDialogs";
import { SelectionBar } from "../components/yourducks/SelectionBar";
import { ArchivedDucks } from "../components/yourducks/ArchivedDucks";
import { useDebounced } from "../hooks/useDebounced";
import { useFlockSearch } from "../hooks/useFlockSearch";
import { useAuth } from "../context/AuthContext";

export const VIEW_KEY = "duckify_your_ducks_view";
export const COLLAPSED_KEY = "duckify_your_ducks_collapsed";

const Tabs = ({ tab, onChange }) => (
  <div className="grid grid-cols-2 gap-1 rounded-full border border-border bg-muted/40 p-1" data-testid="my-ducks-tabs">
    {[["active", "Active", Box], ["archived", "Archived", Archive]].map(([id, label, Icon]) => (
      <button key={id} type="button" data-testid={`my-ducks-tab-${id}`} onClick={() => onChange(id)}
        className={`flex items-center gap-1.5 rounded-full h-9 px-4 text-sm font-semibold transition-colors duration-200 ${tab === id ? "bg-accent text-accent-foreground" : "text-muted-foreground hover:text-foreground"}`}>
        <Icon className="h-4 w-4" /> {label}
      </button>
    ))}
  </div>
);

const SearchEmpty = ({ q, onClear }) => (
  <div data-testid="your-ducks-search-empty" className="mt-20 grid place-items-center gap-3 text-center text-muted-foreground">
    <SearchX className="h-8 w-8" />
    <p>No ducks match “{q}”.</p>
    <Button variant="outline" data-testid="your-ducks-search-clear" className="rounded-full" onClick={onClear}>Clear the search</Button>
  </div>
);

// /my-ducks — Your Ducks: every duck the signed-in maker owns, in flocks (named groups) + Unsorted, as a grid or a list.
// Searching shows only the flocks with a match, expanded, each header saying "3 of 12".
export default function MyDucksPage() {
  const navigate = useNavigate();
  const { sessionReady } = useAuth();
  const [tab, setTab] = useState("active");
  const [view, setView] = usePref(VIEW_KEY, "grid");
  const [collapsed, setCollapsed] = usePref(COLLAPSED_KEY, {});
  const [filters, setFilters] = useState(NO_FILTERS);
  const q = useDebounced(filters.q.trim(), 250);
  const query = useMemo(() => ({ q, status: filters.status, from: filters.from, to: filters.to }), [q, filters.status, filters.from, filters.to]);
  const filtering = !!(query.q || query.status || query.from || query.to);
  const flocks = useFlocks(sessionReady);
  const search = useFlockSearch(query, sessionReady && tab === "active");
  const [reload, setReload] = useState(0);
  const [selected, setSelected] = useState(() => new Map());
  const [naming, setNaming] = useState(null); // {flock?} — null closed, {} = new flock
  const [deleting, setDeleting] = useState(null);
  const [moving, setMoving] = useState(false);

  const refresh = () => {
    clearGalleryCache();
    setReload((n) => n + 1);
    flocks.load();
  };
  const actions = useDuckActions({
    onOpenItem: (c) => navigate(`/?open=${c.id}`),
    onDelete: async (id) => {
      await api.delete(`/creations/${id}`);
      setSelected((m) => { const n = new Map(m); n.delete(id); return n; });
      refresh();
      toast.success("Duck removed.");
    },
    onReload: refresh,
    onRenamed: () => setReload((n) => n + 1),
  });
  const ctx = {
    actions,
    q: search.ready ? query.q : "",
    selected,
    setCover: flocks.setCover,
    toggle: (c) => setSelected((m) => { const n = new Map(m); if (n.has(c.id)) n.delete(c.id); else n.set(c.id, c); return n; }),
  };
  const move = async (flockId) => {
    setMoving(true);
    const ok = await flocks.move([...selected.keys()], flockId);
    setMoving(false);
    if (ok) {
      setSelected(new Map());
      setReload((n) => n + 1);
    }
    return !!ok;
  };
  let sections = [...flocks.flocks.map((f) => ({ key: f.id, flock: f, count: f.count }))];
  if (flocks.unsorted > 0 || !flocks.flocks.length || filtering) sections.push({ key: "unsorted", flock: null, count: flocks.unsorted });
  if (search.ready) sections = sections.filter((s) => search.matchesOf(s.key) > 0);
  const clearSearch = () => setFilters((f) => ({ ...f, q: "" }));
  const empty = flocks.loaded && !flocks.failed && flocks.total === 0 && !filtering;

  return (
    <div className="App min-h-screen bg-dots pb-28" data-testid="my-ducks-page">
      <DuckDownloadModal actions={actions} />
      <main className="max-w-6xl mx-auto px-4 sm:px-8 py-10">
        <Button variant="ghost" className="rounded-full gap-2 -ml-3" data-testid="my-ducks-back-btn" onClick={() => navigate("/")}>
          <ArrowLeft className="h-4 w-4" /> Back to the studio
        </Button>
        <div className="mt-4 flex flex-wrap items-end justify-between gap-4">
          <div>
            <span className="text-xs font-mono2 uppercase tracking-widest text-accent font-semibold">Your collection</span>
            <h1 className="font-heading text-4xl sm:text-5xl font-extrabold tracking-tight mt-2" data-testid="my-ducks-title">Your Ducks</h1>
            <p className="mt-2 text-sm text-muted-foreground" data-testid="my-ducks-count">
              {!flocks.loaded || flocks.failed ? "Loading…" : `${flocks.total} duck${flocks.total === 1 ? "" : "s"} · ${flocks.flocks.length} flock${flocks.flocks.length === 1 ? "" : "s"}`}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Tabs tab={tab} onChange={setTab} />
            {tab === "active" && <ViewToggle view={view} onChange={setView} />}
            {tab === "active" && (
              <Button data-testid="your-ducks-new-flock-btn" className="rounded-full h-11 gap-2 font-bold" disabled={flocks.flocks.length >= flocks.max} onClick={() => setNaming({})}>
                <FolderPlus className="h-4 w-4" /> New flock
              </Button>
            )}
          </div>
        </div>

        <div className="mt-6">
          <DuckFilters value={filters} onChange={setFilters} searchOnly={tab === "archived"} />
        </div>

        {tab === "archived" ? (
          <ArchivedDucks q={q} actions={actions} reload={reload} enabled={sessionReady} />
        ) : flocks.failed ? (
          <div data-testid="my-ducks-error" className="mt-20 text-center text-muted-foreground">
            Your ducks could not be loaded.{" "}
            <Button variant="link" data-testid="my-ducks-retry-btn" className="px-1" onClick={flocks.load}>Try again</Button>
          </div>
        ) : empty ? (
          <div data-testid="my-ducks-empty" className="mt-20 text-center text-muted-foreground">No ducks yet — go make your first one!</div>
        ) : search.searching && !search.counts ? (
          <p data-testid="your-ducks-searching" className="mt-16 flex items-center justify-center gap-2 text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Searching…
          </p>
        ) : search.ready && !search.counts.total ? (
          <SearchEmpty q={query.q} onClear={clearSearch} />
        ) : (
          sections.map((s, k) => (
            <FlockSection
              key={s.key}
              flock={s.flock}
              count={s.count}
              matches={search.ready ? search.matchesOf(s.key) : undefined}
              open={search.ready ? !search.folded[s.key] : !collapsed[s.key]}
              enabled={sessionReady}
              onToggle={() => (search.ready ? search.fold(s.key) : setCollapsed((c) => ({ ...c, [s.key]: !c[s.key] })))}
              view={view}
              filters={query}
              filtering={filtering}
              reload={reload}
              ctx={ctx}
              first={search.ready || k === 0}
              last={search.ready || k === flocks.flocks.length - 1}
              onUp={() => flocks.shift(s.key, -1)}
              onDown={() => flocks.shift(s.key, 1)}
              onRename={() => setNaming({ flock: s.flock })}
              onDelete={() => setDeleting(s.flock)}
            />
          ))
        )}
      </main>

      <SelectionBar selected={selected} flocks={flocks.flocks} busy={moving || actions.basketBusy} onMove={move} onBasket={(ids) => actions.basketAdd(ids)} onClear={() => setSelected(new Map())} />
      <FlockNameDialog
        open={!!naming}
        onOpenChange={(o) => !o && setNaming(null)}
        initial={naming?.flock?.name || ""}
        title={naming?.flock ? "Rename flock" : "New flock"}
        cta={naming?.flock ? "Save name" : "Create flock"}
        onSubmit={(name) => (naming?.flock ? flocks.rename(naming.flock.id, name) : flocks.create(name))}
      />
      <DeleteFlockDialog
        flock={deleting}
        onOpenChange={(o) => !o && setDeleting(null)}
        onConfirm={async () => {
          if (await flocks.remove(deleting)) setReload((n) => n + 1);
          setDeleting(null);
        }}
      />
    </div>
  );
}
